import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import logger from "../../../utils/logger";
import { useOnboarding } from "../../../contexts/OnboardingContext";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const UsernameStep = () => {
  const { username, setUsername, nextStep, currentStep } = useOnboarding();
  const [localUsername, setLocalUsername] = useState(username || "");
  const [isLoading, setIsLoading] = useState(false);
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    logger.log("👤 UsernameStep mounted:", { username, localUsername, currentStep, hasContent: !!localUsername.trim() });
  }, [username, localUsername, currentStep]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById("onboarding-continue-slot");
      if (el) setPortalTarget(el);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleContinue = async () => {
    const trimmedUsername = localUsername.trim();
    if (!trimmedUsername) return;

    setIsLoading(true);
    try {
      setUsername(trimmedUsername);
      nextStep();
    } catch (error) {
      logger.error("❌ Error in handleContinue:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && localUsername.trim() && !isLoading) {
      e.preventDefault();
      handleContinue();
    }
  };

  const canContinue = localUsername.trim().length > 0 && !isLoading;

  const continueButton = (
    <button
      onClick={handleContinue}
      disabled={!canContinue}
      className="inline-flex items-center gap-2 transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        ...FREDOKA,
        background: "#0065F8",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 15,
        border: "none",
        borderRadius: 999,
        padding: "12px 22px",
        boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
        cursor: canContinue ? "pointer" : "not-allowed",
      }}
    >
      {isLoading ? (
        <>
          <span style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.30)", borderTopColor: "#FFFFFF", animation: "ob-spin 1s linear infinite" }} />
          Loading…
        </>
      ) : (
        <>
          Continue
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </>
      )}
    </button>
  );

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4 animate-fadeIn">
      {/* Panda + Speech Bubble */}
      <div className="flex items-start gap-4 sm:gap-5 mb-10 sm:mb-14 w-full">
        <div className="flex-shrink-0">
          <div className="relative w-24 h-24 sm:w-32 sm:h-32">
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,101,248,0.15)", filter: "blur(28px)", borderRadius: "50%", transform: "scale(1.4)" }} />
            <img
              src="/assets/pixley-panda-nobg.png"
              alt="Pixley Panda"
              className="relative w-full h-full object-contain z-10"
              onError={(e) => { e.target.src = "/assets/pixley-panda.png"; }}
              style={{ filter: "drop-shadow(0px 6px 12px rgba(11,26,51,0.18))" }}
            />
          </div>
        </div>

        <div className="flex-1 relative">
          <div
            className="absolute z-0"
            style={{
              left: -8,
              top: 28,
              width: 16,
              height: 16,
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRight: "none",
              borderTop: "none",
              transform: "rotate(45deg)",
            }}
          />
          <div
            className="relative z-10 px-6 py-5 sm:px-7 sm:py-6"
            style={{
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRadius: 22,
              boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.06)",
            }}
          >
            <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              First, a name
            </span>
            <h1
              style={{
                ...FREDOKA,
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: "clamp(1.4rem, 2.6vw, 1.9rem)",
                textTransform: "uppercase",
                letterSpacing: "-0.01em",
                lineHeight: 1.1,
                margin: "6px 0 6px",
              }}
            >
              What's your child's <span style={{ color: "#0065F8" }}>name?</span>
            </h1>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>
              Every great story begins with a hero. Let's name yours.
            </p>
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="w-full max-w-md">
        <div className="relative">
          <input
            type="text"
            value={localUsername}
            onChange={(e) => setLocalUsername(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type name here…"
            maxLength={20}
            className="w-full text-center transition-all"
            style={{
              ...FREDOKA,
              background: "#FFFFFF",
              color: "#0B1A33",
              fontSize: "clamp(1.1rem, 2vw, 1.4rem)",
              fontWeight: 600,
              border: "2px solid rgba(11,26,51,0.08)",
              borderRadius: 18,
              padding: "18px 24px",
              outline: "none",
              boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12), 0px 2px 0px rgba(11,26,51,0.04)"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.08)"; e.currentTarget.style.boxShadow = "0px 2px 0px rgba(11,26,51,0.04)"; }}
            autoFocus
            disabled={isLoading}
          />
        </div>

        <p
          className="mt-4 text-center"
          style={{ ...FREDOKA, color: "#94A3B8", fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase" }}
        >
          The name will be woven into every tale
        </p>

        {localUsername && (
          <p className="text-center mt-2" style={{ ...FREDOKA, color: "#94A3B8", fontSize: 11 }}>
            {localUsername.length}/20
          </p>
        )}
      </div>

      {portalTarget && ReactDOM.createPortal(continueButton, portalTarget)}

      <style>{`@keyframes ob-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default UsernameStep;
